// src/utils/languageManager.js - 统一的语言管理工具
import store from '@/store'
import { setI18nLanguage, supportedLanguages, defaultLanguage } from '@/lang/index'

/**
 * 语言管理器类
 * 提供统一的语言切换、存储和同步功能
 */
export class LanguageManager {
  constructor() {
    this.initialized = false
    this.observers = new Set()
  }

  /**
   * 初始化语言管理器
   */
  async initialize() {
    if (this.initialized) return

    try {
      await store.dispatch('user/initLanguage')
      this.initialized = true
      console.log('Language Manager initialized successfully')
    } catch (error) {
      console.error('Language Manager initialization failed:', error)
      throw error
    }
  }

  /**
   * 切换语言
   * @param {string} langCode - 语言代码
   * @param {Object} options - 选项配置
   */
  async switchLanguage(langCode, options = {}) {
    const { updateRoute = true, forceUpdate = false } = options

    if (!this.isSupported(langCode)) {
      throw new Error(`Language ${langCode} is not supported`)
    }

    const currentLang = this.getCurrentLanguage()
    if (!forceUpdate && currentLang === langCode) {
      return { success: true, changed: false }
    }

    try {
      // 更新 Vuex 状态
      await store.dispatch('user/toggleLanguage', langCode)
      
      // 更新 vue-i18n
      setI18nLanguage(langCode)
      
      // 更新 HTML lang 属性
      document.documentElement.setAttribute('lang', langCode)
      
      // 通知观察者
      this.notifyObservers({
        type: 'language-changed',
        from: currentLang,
        to: langCode,
        timestamp: Date.now()
      })

      return { success: true, changed: true, from: currentLang, to: langCode }
    } catch (error) {
      console.error('Language switch failed:', error)
      throw error
    }
  }

  /**
   * 获取当前语言代码
   */
  getCurrentLanguage() {
    return store.getters['user/currentLanguageCode'] || defaultLanguage
  }

  /**
   * 获取当前语言对象
   */
  getCurrentLanguageObject() {
    return store.getters['user/currentLanguage'] || { country: defaultLanguage }
  }

  /**
   * 检查语言是否支持
   */
  isSupported(langCode) {
    return supportedLanguages.includes(langCode)
  }

  /**
   * 获取支持的语言列表
   */
  getSupportedLanguages() {
    return [...supportedLanguages]
  }

  /**
   * 获取可用语言列表（包含名称）
   */
  getAvailableLanguages() {
    return store.getters['user/availableLanguages'] || []
  }

  /**
   * 从浏览器语言检测默认语言
   */
  detectBrowserLanguage() {
    const browserLang = navigator.language.split('-')[0]
    return this.isSupported(browserLang) ? browserLang : defaultLanguage
  }

  /**
   * 重置语言为默认值
   */
  async resetToDefault() {
    return await this.switchLanguage(defaultLanguage, { forceUpdate: true })
  }

  /**
   * 添加语言变化观察者
   */
  addObserver(callback) {
    if (typeof callback === 'function') {
      this.observers.add(callback)
      return () => this.observers.delete(callback) // 返回移除函数
    }
  }

  /**
   * 移除语言变化观察者
   */
  removeObserver(callback) {
    return this.observers.delete(callback)
  }

  /**
   * 通知所有观察者
   */
  notifyObservers(event) {
    this.observers.forEach(callback => {
      try {
        callback(event)
      } catch (error) {
        console.error('Observer callback failed:', error)
      }
    })
  }

  /**
   * 清理所有观察者
   */
  clearObservers() {
    this.observers.clear()
  }

  /**
   * 获取语言的本地化名称
   */
  getLanguageDisplayName(langCode, inLanguage = null) {
    const names = {
      'en': { en: 'English', cn: 'English', es: 'Inglés', fr: 'Anglais', by: 'Английский' },
      'cn': { en: 'Chinese', cn: '中文', es: 'Chino', fr: 'Chinois', by: 'Китайский' },
      'es': { en: 'Spanish', cn: '西班牙语', es: 'Español', fr: 'Espagnol', by: 'Испанский' },
      'fr': { en: 'French', cn: '法语', es: 'Francés', fr: 'Français', by: 'Французский' },
      'by': { en: 'Russian', cn: '俄语', es: 'Ruso', fr: 'Russe', by: 'Русский' },
      'de': { en: 'Germany', cn: '德语', es: 'Alemania', fr: 'Allemagne', by: 'Германия' },
      'it': { en: 'Italy', cn: '意大利语', es: 'Italia', fr: 'Italie', by: 'Италия' },
    }

    const targetLang = inLanguage || this.getCurrentLanguage()
    return names[langCode]?.[targetLang] || langCode.toUpperCase()
  }

  /**
   * 验证并修复语言设置
   */
  validateAndRepair() {
    const currentLang = this.getCurrentLanguage()
    if (!this.isSupported(currentLang)) {
      console.warn(`Current language ${currentLang} is not supported, resetting to default`)
      return this.resetToDefault()
    }
    return Promise.resolve({ success: true, repaired: false })
  }
}

// 创建单例实例
export const languageManager = new LanguageManager()

// 便捷导出函数
export const switchLanguage = (langCode, options) => languageManager.switchLanguage(langCode, options)
export const getCurrentLanguage = () => languageManager.getCurrentLanguage()
export const isLanguageSupported = (langCode) => languageManager.isSupported(langCode)
export const getSupportedLanguages = () => languageManager.getSupportedLanguages()
export const detectBrowserLanguage = () => languageManager.detectBrowserLanguage()

// 初始化语言管理器（在应用启动时调用）
export const initializeLanguageManager = () => languageManager.initialize()

export default languageManager